import React, { useEffect, useRef } from "react";
import { Button, Typography } from "@mui/material";
import { DtDialog } from "./DtDialog";

type DialogMaxWidth = "xs" | "sm" | "md" | "lg" | "xl"; // match DtDialog

interface DtConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: React.ReactNode;
  message: React.ReactNode;
  subMessage?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmColor?: "primary" | "error" | "secondary";
  loading?: boolean;
  maxWidth?: DialogMaxWidth; // use correct type
}

const DtConfirmDialog: React.FC<DtConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title = "Are you sure?",
  message,
  subMessage,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  confirmColor = "primary",
  loading = false,
  maxWidth, // pass through
}) => {
  const confirmButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) {
      // Focus confirm button after 200ms
      const timeoutId = setTimeout(() => {
        confirmButtonRef.current?.focus();
      }, 200);
      
      return () => clearTimeout(timeoutId);
    }
  }, [open]);

  return (
    <DtDialog
      open={open}
      onClose={onClose}
      title={title}
      maxWidth={maxWidth}
      actions={
        <>
          <Button variant="outlined" size="small" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            ref={confirmButtonRef}
            onClick={onConfirm}
            color={confirmColor}
            variant="contained"
            disabled={loading}
            size="small"
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <Typography>{message}</Typography>
      {subMessage && <Typography variant="body2" color="textSecondary" sx={{ marginTop: 1 }}>
        {subMessage}
      </Typography>}
    </DtDialog>
  );
};

export { DtConfirmDialog };